import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { formatDate } from '../../lib/format';
import { AbonnementDialog } from './AbonnementDialog';
import { AbonnementBewerkDialog } from './AbonnementBewerkDialog';
import type { OrderInfo } from '../../stores/klanten-store';
import { useUserRole } from '../../hooks/useUserRole';

/**
 * Klantpagina-sectie service-abonnementen (fase 1 + 1.5): lopende abonnementen
 * + aanmaken/bewerken/pauzeren/hervatten/opzeggen, extra wissel en de
 * wissel-planning-status per laatste run. Data via GET/POST/PATCH
 * /api/abonnementen; de dagelijkse crons (abonnementen-fire + -wissel) doen
 * de rest.
 */
interface AbonnementRow {
  id: string;
  naam: string;
  brand: string;
  interval_weeks: number;
  next_run_at: string;
  korting_percentage: number | null;
  bevestigingen_sturen: boolean;
  opmerkingen_planning: string | null;
  eindigt_op: string | null;
  status: 'active' | 'paused' | 'cancelled';
  /** b2b (matwissel) of kleed (B2C kleed-abonnement, plan 2026-10-22). */
  type?: 'b2b' | 'kleed';
  overslaan_count?: number;
  seizoen_label?: string | null;
  bron?: string | null;
  laatste_run: {
    period_key: string;
    status: string;
    sf_werk_order_name: string | null;
    error: string | null;
    fired_at: string;
    wissel_status: string | null;
    geplande_wissel_datum: string | null;
    is_extra: boolean | null;
  } | null;
}

interface AbonnementenSectionProps {
  accountId: string;
  orders: OrderInfo[];
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (session?.access_token) headers['Authorization'] = `Bearer ${session.access_token}`;
  return headers;
}

const RUN_STATUS_LABELS: Record<string, string> = {
  created: 'order aangemaakt',
  'dry-run': 'dry-run',
  pending: 'bezig…',
  failed: 'mislukt',
};

/** Zelfde semantiek als wisselStatusLabel in api/_lib/abonnementen-wissel.ts. */
const WISSEL_STATUS_LABELS: Record<string, string> = {
  'nog-niet': 'wissel nog niet gepland',
  'wacht-op-klaar': 'wacht op klaarmelding',
  'wacht-op-route': 'wacht op route',
  gepland: 'wissel gepland',
  handmatig: 'handmatig inplannen (ops-taak)',
  'dry-run': 'wissel dry-run',
};

function wisselBadgeClass(status: string): string {
  switch (status) {
    case 'gepland':
      return 'text-emerald-700 bg-emerald-50 border-emerald-200';
    case 'handmatig':
      return 'text-red-700 bg-red-50 border-red-200';
    case 'dry-run':
      return 'text-slate-600 bg-slate-50 border-slate-200';
    default:
      return 'text-amber-700 bg-amber-50 border-amber-200';
  }
}

export function AbonnementenSection({ accountId, orders }: AbonnementenSectionProps) {
  const [abonnementen, setAbonnementen] = useState<AbonnementRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bewerkAbonnement, setBewerkAbonnement] = useState<AbonnementRow | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { role } = useUserRole();
  const kanMuteren = role === 'admin' || role === 'medewerker';

  const load = useCallback(async () => {
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/abonnementen?accountId=${encodeURIComponent(accountId)}`, { headers });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setAbonnementen((data.abonnementen as AbonnementRow[]) ?? []);
        setError(null);
      } else {
        setError(data.error || 'Kon abonnementen niet laden');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    if (accountId) void load();
  }, [accountId, load]);

  const patchStatus = async (id: string, status: 'active' | 'paused' | 'cancelled') => {
    if (status === 'cancelled' && !window.confirm('Abonnement definitief opzeggen? Er worden geen orders meer aangemaakt.')) {
      return;
    }
    setBusyId(id);
    try {
      const headers = await authHeaders();
      const res = await fetch(`/api/abonnementen?id=${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify({ status }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) setError(data.error || 'Bijwerken mislukt');
      else await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Onverwachte fout');
    } finally {
      setBusyId(null);
    }
  };

  const zichtbaar = abonnementen.filter((a) => a.status !== 'cancelled');
  // Sectie verbergen zolang er niets is: de knop blijft de enige ingang.
  if (loading && zichtbaar.length === 0) return null;

  return (
    <section className="bg-white border border-slate-200 rounded-lg px-4 py-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Abonnementen</h3>
        {kanMuteren && (
          <button
            type="button"
            onClick={() => setDialogOpen(true)}
            className="text-xs font-medium text-indigo-600 hover:text-indigo-700 hover:underline"
          >
            + Abonnement toevoegen
          </button>
        )}
      </div>

      {error && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2 mb-2">{error}</div>
      )}

      {zichtbaar.length === 0 ? (
        <p className="text-xs text-slate-500 italic">Geen lopende abonnementen.</p>
      ) : (
        <ul className="space-y-2">
          {zichtbaar.map((a) => (
            <li key={a.id} className="border border-slate-200 rounded px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium text-slate-900 truncate">
                    {a.naam}
                    {a.status === 'paused' && (
                      <span className="ml-2 text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                        Gepauzeerd
                      </span>
                    )}
                    {a.type === 'kleed' && (
                      <span className="ml-2 text-[11px] font-medium text-sky-700 bg-sky-50 border border-sky-200 rounded px-1.5 py-0.5">
                        Kleed-abonnement{a.overslaan_count ? ` · 1x overgeslagen` : ''}{a.bron ? ` · ${a.bron}` : ''}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    Elke {a.interval_weeks} weken · volgende: {formatDate(a.next_run_at)}
                    {a.eindigt_op ? ` · t/m ${formatDate(a.eindigt_op)}` : ''}
                    {a.korting_percentage ? ` · ${a.korting_percentage}% korting` : ''}
                    {!a.bevestigingen_sturen && ' · geen klant-mails'}
                  </div>
                  {a.laatste_run && (
                    <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      <span>
                        Laatste run ({formatDate(a.laatste_run.fired_at)}
                        {a.laatste_run.period_key ? `, cyclus ${formatDate(a.laatste_run.period_key)}` : ''}
                        {a.laatste_run.is_extra ? ', extra' : ''}):{' '}
                        <span className={a.laatste_run.status === 'failed' ? 'text-red-600 font-medium' : ''}>
                          {RUN_STATUS_LABELS[a.laatste_run.status] ?? a.laatste_run.status}
                        </span>
                        {a.laatste_run.sf_werk_order_name && ` · ${a.laatste_run.sf_werk_order_name}`}
                      </span>
                      {a.laatste_run.wissel_status && a.laatste_run.wissel_status !== 'nog-niet' && (
                        <span
                          className={`text-[11px] font-medium border rounded px-1.5 py-0.5 ${wisselBadgeClass(a.laatste_run.wissel_status)}`}
                        >
                          {WISSEL_STATUS_LABELS[a.laatste_run.wissel_status] ?? a.laatste_run.wissel_status}
                          {a.laatste_run.geplande_wissel_datum &&
                            (a.laatste_run.wissel_status === 'gepland' || a.laatste_run.wissel_status === 'dry-run') &&
                            `: ${formatDate(a.laatste_run.geplande_wissel_datum)}`}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {kanMuteren && (
                  <div className="flex items-center gap-2 shrink-0">
                    <button
                      type="button"
                      disabled={busyId === a.id}
                      onClick={() => setBewerkAbonnement(a)}
                      className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline disabled:opacity-50"
                    >
                      Bewerken
                    </button>
                    {a.status === 'active' ? (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => patchStatus(a.id, 'paused')}
                        className="text-xs text-slate-600 hover:text-slate-900 hover:underline disabled:opacity-50"
                      >
                        Pauzeren
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={busyId === a.id}
                        onClick={() => patchStatus(a.id, 'active')}
                        className="text-xs text-indigo-600 hover:text-indigo-700 hover:underline disabled:opacity-50"
                      >
                        Hervatten
                      </button>
                    )}
                    <button
                      type="button"
                      disabled={busyId === a.id}
                      onClick={() => patchStatus(a.id, 'cancelled')}
                      className="text-xs text-red-600 hover:text-red-700 hover:underline disabled:opacity-50"
                    >
                      Opzeggen
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      <AbonnementDialog
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        accountId={accountId}
        orders={orders}
        onCreated={() => void load()}
      />
      {bewerkAbonnement && (
        <AbonnementBewerkDialog
          key={bewerkAbonnement.id}
          isOpen
          onClose={() => setBewerkAbonnement(null)}
          abonnement={bewerkAbonnement}
          onSaved={() => void load()}
        />
      )}
    </section>
  );
}
